import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Badge,
  Button,
  cn,
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
  FieldTitle,
  Flex,
  Icon,
  Input,
  RadioGroup,
  RadioGroupItem,
  Skeleton,
  Text,
} from '@kognitos/lattice';

import { getNoAutofillProps } from '@/shared/utils';
import { formatRelativeTime } from '@/shared/utils/datetime-utils';

import { CredentialActionsMenu } from './CredentialActionsMenu';
import { CredentialStatusDot } from './CredentialStatusDot';

import { ECredentialScope, type TCredential } from '../types';
import { getCredentialScope } from '../utils';

/** Props for {@link CredentialPicker}. */
export interface ICredentialPickerProps {
  /** Compatible credentials to choose from. */
  credentials: TCredential[];
  /** Id of the selected credential, if any. */
  selectedCredentialId?: string;
  /** Called with the id of the picked credential. */
  onSelect: (credentialId: string) => void;
  /** Switches to creating a new credential; omit to offer existing ones only. */
  onCreate?: () => void;
  /** Overrides the default description under the label. */
  description?: string;
  /** Replaces the default "Create a new credential" button. */
  createAction?: ReactNode;
  /** Overrides the default "Create a new credential" text. */
  createLabel?: string;
  /** Shows a loading placeholder instead of the list. */
  isLoading?: boolean;
  /** Prefix for option element ids (unique per picker on the page). */
  idPrefix: string;
  /** The ⋯ actions offered for each credential; omitted ones are hidden. */
  actionsFor?: (credential: TCredential) => ICredentialOptionActions;
}

/** Actions on one credential of the list. */
export interface ICredentialOptionActions {
  /** Renames in place; resolves to an error to show, or nothing once renamed. */
  rename?: (displayName: string) => Promise<string | undefined>;
  editValues?: () => void;
  delete?: () => void;
}

// The card look FieldLabel gives a wrapped Field, applied to the Field itself.
const optionClassName =
  'w-full !items-center !gap-0 rounded-lg border transition-colors duration-200 has-data-[state=checked]:border-primary has-data-[state=checked]:bg-primary/5 dark:has-data-[state=checked]:bg-primary/10';

/**
 * Lists the compatible credentials a connection can use, with an action to
 * create a new one instead.
 *
 * @param props - Component props.
 * @returns The credential picker.
 */
export function CredentialPicker({
  credentials,
  selectedCredentialId,
  onSelect,
  onCreate,
  description,
  createAction,
  createLabel,
  isLoading = false,
  idPrefix,
  actionsFor,
}: ICredentialPickerProps) {
  const { t } = useTranslation('credentials');
  const [renamingId, setRenamingId] = useState<string>();
  const [nameDraft, setNameDraft] = useState('');
  const [nameError, setNameError] = useState<string>();
  const [isSavingName, setIsSavingName] = useState(false);

  const finishRename = async (
    credential: TCredential,
    rename: NonNullable<ICredentialOptionActions['rename']>,
  ) => {
    if (isSavingName) return;
    const displayName = nameDraft.trim();
    if (displayName === (credential.display_name || credential.name)) {
      setRenamingId(undefined);
      return;
    }
    setIsSavingName(true);
    try {
      const error = await rename(displayName);
      if (error) {
        setNameError(error);
        return;
      }
      setRenamingId(undefined);
    } catch {
      setNameError(t('credentials.errors.updateFailed'));
    } finally {
      setIsSavingName(false);
    }
  };

  if (isLoading) {
    return (
      <Flex direction="column" align="stretch" gap={2} width="full">
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
      </Flex>
    );
  }

  const scopeLabel = (credential: TCredential) =>
    getCredentialScope(credential) === ECredentialScope.Organization
      ? t('credentials.scope.organization')
      : t('credentials.scope.workspace');

  return (
    <Flex direction="column" align="stretch" gap={3} width="full">
      <Flex direction="column" gap={1}>
        <Text weight="medium" level="small">
          {t('credentials.picker.label')}
        </Text>
        <Text level="small" color="muted">
          {description ??
            (onCreate
              ? t('credentials.picker.description')
              : t('credentials.picker.descriptionExistingOnly'))}
        </Text>
      </Flex>

      {credentials.length > 0 ? (
        <RadioGroup
          value={selectedCredentialId ?? ''}
          onValueChange={onSelect}
          className="flex w-full flex-col gap-2"
        >
          {credentials.map(credential => {
            const credentialId = credential.id ?? '';
            const optionId = `${idPrefix}-${credentialId}`;
            const actions = actionsFor?.(credential) ?? {};
            const isRenaming = renamingId === credentialId && !!actions.rename;
            const hasActions =
              !!actions.rename || !!actions.editValues || !!actions.delete;
            return (
              // The label fills the card so all of it selects the option; the
              // ⋯ menu sits beside it, since a label can't hold other controls.
              <Field
                key={credentialId}
                orientation="horizontal"
                className={cn(optionClassName, hasActions && 'pr-2')}
              >
                {isRenaming && actions.rename ? (
                  <Flex align="center" gap={2} className="min-w-0 flex-1 p-3">
                    <RadioGroupItem value={credentialId} id={optionId} />
                    <Flex direction="column" gap={1} className="min-w-0 flex-1">
                      <Input
                        {...getNoAutofillProps()}
                        autoFocus
                        aria-label={t('credentials.create.displayName.label')}
                        value={nameDraft}
                        disabled={isSavingName}
                        onChange={event => {
                          setNameDraft(event.target.value);
                          setNameError(undefined);
                        }}
                        onBlur={() =>
                          void finishRename(credential, actions.rename!)
                        }
                        onKeyDown={event => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            void finishRename(credential, actions.rename!);
                          } else if (event.key === 'Escape') {
                            event.preventDefault();
                            event.stopPropagation();
                            setRenamingId(undefined);
                          }
                        }}
                      />
                      {nameError && (
                        <Text level="small" className="text-destructive">
                          {nameError}
                        </Text>
                      )}
                    </Flex>
                  </Flex>
                ) : (
                  <FieldLabel
                    htmlFor={optionId}
                    // The card shows the selected tint; FieldLabel would add its own.
                    className="w-auto min-w-0 flex-1 cursor-pointer items-center gap-2 self-stretch p-3 !font-normal has-data-[state=checked]:bg-transparent dark:has-data-[state=checked]:bg-transparent"
                  >
                    <RadioGroupItem value={credentialId} id={optionId} />
                    <FieldContent className="min-w-0">
                      <Flex align="center" gap={2} className="min-w-0">
                        <CredentialStatusDot credential={credential} />
                        <FieldTitle className="!font-normal truncate">
                          {credential.display_name || credential.name}
                        </FieldTitle>
                        <Badge variant="outline">
                          {scopeLabel(credential)}
                        </Badge>
                      </Flex>
                      {credential.update_time && (
                        <FieldDescription>
                          {t('credentials.picker.updated', {
                            when: formatRelativeTime(credential.update_time),
                          })}
                        </FieldDescription>
                      )}
                    </FieldContent>
                  </FieldLabel>
                )}
                <CredentialActionsMenu
                  actions={{
                    onRename: actions.rename
                      ? () => {
                          setNameDraft(
                            credential.display_name || credential.name || '',
                          );
                          setNameError(undefined);
                          setRenamingId(credentialId);
                        }
                      : undefined,
                    onEditValues: actions.editValues,
                    onDelete: actions.delete,
                  }}
                />
              </Field>
            );
          })}
        </RadioGroup>
      ) : (
        !onCreate && (
          <Text level="small" color="muted">
            {t('credentials.picker.empty')}
          </Text>
        )
      )}

      {createAction ??
        (onCreate && (
          <Button
            type="button"
            variant="outline"
            className="h-auto w-full justify-start border-dashed !p-3 font-normal"
            onClick={onCreate}
          >
            <Icon type="Plus" size="sm" />
            {createLabel ?? t('credentials.picker.createNew')}
          </Button>
        ))}
    </Flex>
  );
}
